import isEmpty from 'lodash-es/isEmpty';
import { BehaviorSubject } from 'rxjs';
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, Inject,
  Input, OnDestroy, Output, ViewEncapsulation
} from '@angular/core';

import { isNullOrUndefined } from '@app/util/util';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { SearchType } from '@search-bar/entities/search-type';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { TimeField } from '@shared/base-search-bar/entities/time.entities';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';

@Component({
  selector: 'app-auto-search-datetime',
  templateUrl: './auto-search-datetime.component.html',
  styleUrls: ['./auto-search-datetime.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class AutoSearchDatetimeComponent implements OnDestroy {
  public activeCalendarType: CalendarField = CalendarField.departure;
  public eventPath: BehaviorSubject<EventTarget[]> = new BehaviorSubject<EventTarget[]>([]);

  @Input() searchType: SearchType = SearchType.HOME_AUTOS;
  @Input() source: EVENT_SOURCE | string;
  @Input() showSearch: boolean = false;
  @Input() showAutoDetail: boolean;
  @Input() isMobile: boolean;
  @Input() isSubmitted: boolean;
  @Input() dropOffDate: Date;
  @Input() pickUpDate: Date;
  @Input() dropOffTime: string = '';
  @Input() pickUpTime: string = '';
  @Input() showCalendarInput: boolean;

  @Output() showMobileCalendarEvent: EventEmitter<CalendarField> = new EventEmitter<CalendarField>();
  @Output() showMobileTimeEvent: EventEmitter<TimeField> = new EventEmitter<TimeField>();
  @Output() selectCalendarDates: EventEmitter<Date[]> = new EventEmitter<Date[]>();
  @Output() selectTimes: EventEmitter<string[]> = new EventEmitter<string[]>();

  @HostListener('document:click', ['$event']) clickout(event: Event): void {
    if (this.isBrowser) this.eventPath.next(event.composedPath());
  }

  public readonly icons = Icon;

  constructor(
    private cdr: ChangeDetectorRef,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) { }

  ngOnDestroy(): void {
    this.cdr.detach();
  }

  get isAutoDetail(): boolean {
    return this.searchType === SearchType.AUTOS_DETAIL; 
  }

  get autoSearchHome(): boolean {
    return this.searchType === SearchType.HOME_AUTOS; 
  }

  get calendarField(): typeof CalendarField {
    return CalendarField;
  }

  get timeField(): typeof TimeField {
    return TimeField;
  }

  get isPickUpDateEmpty(): boolean {
    return isNullOrUndefined(this.pickUpDate) && this.isSubmitted;
  }

  public isEmptyDate(date: Date): boolean {
    return isNullOrUndefined(date);
  }

  get isDropOffDateEmpty(): boolean {
    return isNullOrUndefined(this.dropOffDate) && this.isSubmitted;
  }

  get isPickUpTimeEmpty(): boolean {
    return isNullOrUndefined(this.pickUpTime) && this.isSubmitted;
  }

  get isDropOffTimeEmpty(): boolean {
    return isNullOrUndefined(this.dropOffTime) && this.isSubmitted;
  }

  public addErrorClass(field: string): { [key: string]: boolean } {
    return {[field]: true};
  }

  public selectCalendarInput(type: CalendarField): void {
    this.showCalendarInput = true; this.activeCalendarType = type;
  }

  public showMobileCalendar(type: CalendarField): void {
    this.showMobileCalendarEvent.emit(type);
  }

  public showMobileTimePicker(type: TimeField): void {
    this.showMobileTimeEvent.emit(type);
  }

  public hideCalendarInput(): void {
    this.showCalendarInput = false;
  }

  public applyCalendar(event: Date[]): void {
    if (!isEmpty(event)) {
      this.pickUpDate = event[0];
      this.dropOffDate = event[1];
      this.selectCalendarDates.emit(event);
    } this.hideCalendarInput();
  }

  public updateTime(time: string, type: TimeField): void {
    if (type === TimeField.arrival) {
      this.dropOffTime = time;
    } else {
      this.pickUpTime = time;
    }
    this.selectTimes.emit([this.pickUpTime, this.dropOffTime]);
  }
}
